import { useCallback, useEffect, useMemo, useState } from 'react'
import { artifactService, ApiError } from '@/services'
import { useUpdatesStream } from '@/hooks/useUpdatesStream'
import { useAppStore } from '@/stores/appStore'
import { ARTIFACT_CATALOG, CONTEXT_FILES } from '@/lib/artifactCatalog'
import { ArtifactCard } from '@/components/export/ArtifactCard'
import { ContextFileCard } from '@/components/export/ContextFileCard'
import { ArtifactPreviewModal } from '@/components/export/ArtifactPreviewModal'
import type { Artifact, ArtifactType } from '@/types/api'

/**
 * Phase 6 Export & Generate. Two sections:
 *   1. Business Artifacts — live `POST /artifacts` flow, 9 types.
 *   2. AI Context Files — Phase 2, muted "coming soon" cards.
 *
 * Card state derives from the latest `Artifact` per type. SSE refetches
 * on any artifact event so generate → completed transitions land
 * without a manual reload. The preview modal reads `artifact.content`
 * lazily on view click — keeps the list response light.
 */
export function ExportPage() {
  const startupId = useAppStore((s) => s.startup?.id ?? null)

  const [artifacts, setArtifacts] = useState<Artifact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyType, setBusyType] = useState<ArtifactType | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewArtifact, setPreviewArtifact] = useState<Artifact | null>(null)

  const fetchAll = useCallback(async () => {
    if (!startupId) return
    setLoading(true)
    setError(null)
    try {
      const res = await artifactService.list(startupId)
      setArtifacts(res.artifacts ?? [])
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return
      setError(err instanceof Error ? err.message : 'Failed to load exports.')
    } finally {
      setLoading(false)
    }
  }, [startupId])

  useEffect(() => {
    void fetchAll()
  }, [fetchAll])

  useUpdatesStream({
    startupId,
    filters: { entityTypes: ['artifact'] },
    enabled: !!startupId,
    onUpdate: useCallback(() => {
      void fetchAll()
    }, [fetchAll]),
  })

  // Most recent artifact per type. Falls back to undefined when a type
  // hasn't been generated yet — the card collapses to the idle state.
  const latestByType = useMemo(() => {
    const map = new Map<ArtifactType, Artifact>()
    for (const a of artifacts) {
      const existing = map.get(a.type)
      if (!existing || isNewer(a, existing)) {
        map.set(a.type, a)
      }
    }
    return map
  }, [artifacts])

  const handleGenerate = useCallback(
    async (type: ArtifactType) => {
      if (!startupId) return
      setBusyType(type)
      setError(null)
      try {
        await artifactService.generate(startupId, type)
        // SSE refresh will land the real generating + completed artifact.
        // Nudge a refetch right away so the spinner appears even before
        // the first SSE bump (the dev seed sometimes lags by a tick).
        void fetchAll()
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return
        setError(
          err instanceof Error ? err.message : 'Could not start generation.'
        )
      } finally {
        setBusyType(null)
      }
    },
    [startupId, fetchAll]
  )

  const handleView = useCallback(
    async (artifact: Artifact) => {
      if (!startupId) return
      // List response usually omits `content` — refetch the single
      // artifact so the modal renders the full markdown body.
      if (artifact.content && artifact.content.length > 0) {
        setPreviewArtifact(artifact)
        return
      }
      setPreviewLoading(true)
      try {
        const full = await artifactService.get(startupId, artifact.id)
        setPreviewArtifact(full)
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 401)) {
          setError(
            err instanceof Error ? err.message : 'Could not load preview.'
          )
        }
      } finally {
        setPreviewLoading(false)
      }
    },
    [startupId]
  )

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col gap-10">
      <header>
        <h1 className="text-2xl font-display font-semibold text-text-primary tracking-tight">
          Export &amp; Generate
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          Generate business artifacts from your context engine, and
          distribute structured context to your AI tools.
        </p>
      </header>

      {error && (
        <div
          role="alert"
          className="text-sm px-3 py-2 rounded-md border border-warning/30 bg-warning/10 text-text-primary"
        >
          {error}
        </div>
      )}

      <section className="flex flex-col gap-3">
        <header className="flex items-end justify-between gap-3">
          <h2 className="text-lg font-display font-semibold text-text-primary">
            Business Artifacts
          </h2>
          <span className="text-xs text-text-tertiary">
            Generated from your context engine
          </span>
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ARTIFACT_CATALOG.map((def) => {
            const latest = latestByType.get(def.type) ?? null
            return (
              <ArtifactCard
                key={def.type}
                def={def}
                artifact={latest}
                busy={busyType === def.type}
                onGenerate={() => void handleGenerate(def.type)}
                onView={() => latest && void handleView(latest)}
                onRetry={() => void handleGenerate(def.type)}
              />
            )
          })}
        </div>
        {loading && artifacts.length === 0 && (
          <p className="text-xs text-text-tertiary">Loading artifacts…</p>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <header className="flex items-end justify-between gap-3">
          <h2 className="text-lg font-display font-semibold text-text-primary">
            AI Context Files
          </h2>
          <span className="text-xs text-text-tertiary">
            Coming in Phase 2
          </span>
        </header>
        <p className="text-sm text-text-secondary leading-relaxed max-w-2xl">
          One-click exports for Claude Code, Cursor, and other AI coding
          tools. These files package your source of truth into the format
          each tool expects. Generation endpoint is in progress.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CONTEXT_FILES.map((file) => (
            <ContextFileCard key={file.filename} file={file} />
          ))}
        </div>
      </section>

      <ArtifactPreviewModal
        artifact={previewArtifact}
        loading={previewLoading}
        onClose={() => {
          setPreviewArtifact(null)
          setPreviewLoading(false)
        }}
      />
    </div>
  )
}

function isNewer(a: Artifact, b: Artifact): boolean {
  const aTime = Date.parse(a.updatedAt ?? a.createdAt ?? '') || 0
  const bTime = Date.parse(b.updatedAt ?? b.createdAt ?? '') || 0
  return aTime >= bTime
}
